/**
 * 商品閲覧履歴
 * ハンドルのみを保存し、表示時に Section Rendering API で最新データを取得する方式
 */

import Splide from "@splidejs/splide";
import { breakpoint } from "../constant/breakpoint";

// ======================================
// 定数・型定義
// ======================================

const STORAGE_KEY = "hinatalife_viewed_products";
const MAX_ITEMS = 30;

/**
 * 閲覧履歴データ
 */
interface ViewedProductEntry {
  handle: string; // 商品ハンドル
  viewedAt: number; // 閲覧日時（Unix ミリ秒）
}

// ======================================
// 旧ストレージ削除
// ======================================

/**
 * 旧ストレージ（recentlyViewedProducts と viewed_* キー）を削除する
 * 新仕様に移行する際に 1 回だけ実行する
 */
function removeLegacyViewedProductsStorage(): void {
  try {
    // recentlyViewedProducts を削除
    localStorage.removeItem("recentlyViewedProducts");

    // viewed_* プレフィックスのキーを一括削除（任意）
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("viewed_")) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // エラーは無視
  }
}

// ======================================
// 取得・保存
// ======================================

/**
 * ローカルストレージから閲覧履歴を取得
 */
function getViewedProducts(): ViewedProductEntry[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) return [];

    const parsed = JSON.parse(data);
    if (!Array.isArray(parsed)) return [];

    // 有効なエントリのみフィルタ
    const validProducts = parsed.filter((item): item is ViewedProductEntry => {
      return (
        typeof item === "object" &&
        item !== null &&
        typeof item.handle === "string" &&
        typeof item.viewedAt === "number"
      );
    });

    return validProducts;
  } catch {
    return [];
  }
}

/**
 * ローカルストレージから閲覧履歴のハンドルリストを取得
 * @param excludeHandle - 除外するハンドル（現在の商品ページなど）
 */
function getViewedProductHandles(excludeHandle?: string): string[] {
  const products = getViewedProducts();
  return products
    .filter((p) => !excludeHandle || p.handle !== excludeHandle)
    .map((p) => p.handle);
}

/**
 * 閲覧履歴をローカルストレージに保存
 */
function saveViewedProducts(products: ViewedProductEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
  } catch {
    // ストレージ容量超過時などは無視
  }
}

/**
 * 商品を閲覧履歴に追加
 * - 重複時は既存を削除して先頭に追加
 * - MAX_ITEMS 件を超えた場合は古いものを削除
 */
function saveViewedProduct(handle: string): void {
  const products = getViewedProducts();

  // 重複チェック：同じハンドルの商品があれば削除
  const filtered = products.filter((p) => p.handle !== handle);

  // 先頭に追加
  const newEntry: ViewedProductEntry = {
    handle: handle,
    viewedAt: Date.now(),
  };
  filtered.unshift(newEntry);

  // 最大件数を超えた場合は古いものを削除
  const trimmed = filtered.slice(0, MAX_ITEMS);

  saveViewedProducts(trimmed);
}

// ======================================
// 商品詳細ページ：保存
// ======================================

/**
 * 商品詳細ページで実行：商品ハンドルを閲覧履歴に保存
 */
function initProductHistory(): void {
  // window.__PRODUCT_HANDLE__ が存在する場合のみ実行（商品詳細ページ）
  const productHandle = (window as unknown as Record<string, unknown>)
    .__PRODUCT_HANDLE__ as string | undefined;
  if (!productHandle) return;

  saveViewedProduct(productHandle);
}

// ======================================
// トップページ：閲覧履歴セクション
// ======================================

/**
 * 閲覧履歴セクションの初期化
 * - ローカルストレージからハンドルリストを取得
 * - 各商品ページ経由で Section Rendering API で最新の商品データを取得
 * - 履歴がない場合やエラー時はセクションを非表示
 */
async function initHistorySection(): Promise<void> {
  const section = document.querySelector<HTMLElement>(".p-top-recently-viewed");
  if (!section) return;

  const list = section.querySelector<HTMLElement>(".splide__list");
  if (!list) return;

  // 現在の商品詳細ページの場合、そのハンドルを除外
  const currentProductHandle = (window as unknown as Record<string, unknown>)
    .__PRODUCT_HANDLE__ as string | undefined;

  // 閲覧履歴のハンドルリストを取得（現在の商品を除外）
  const handles = getViewedProductHandles(currentProductHandle);

  // 履歴がない場合はセクションを非表示
  if (handles.length === 0) {
    section.style.display = "none";
    return;
  }

  try {
    // 各商品ページから Section Rendering API で商品カード HTML を取得（並列処理）
    const fetchPromises = handles.map(async (handle: string) => {
      try {
        const response = await fetch(
          `/products/${handle}?section_id=history-product-card`,
        );
        if (!response.ok) {
          // 404 等（削除済み商品）はスキップ
          return null;
        }
        const html = await response.text();
        // DOMParser で HTML を安全にパースし、div.splide__slide タグを抽出
        const doc = new DOMParser().parseFromString(html, "text/html");
        // 修正: history-product-card の li を div に変更したため querySelector を更新
        const li = doc.querySelector("div.splide__slide");
        return li ? li.outerHTML : null;
      } catch {
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);

    // null を除去して有効な HTML のみ結合
    const validHtmls = results.filter((html) => html !== null);

    // 有効な商品がない場合はセクションを非表示
    if (validHtmls.length === 0) {
      section.style.display = "none";
      return;
    }

    // リストに HTML を挿入
    list.innerHTML = validHtmls.join("");

    // セクションを表示
    section.style.display = "";

    // Splide スライダーを初期化
    const sliderElement = section.querySelector<HTMLElement>(".splide");
    if (sliderElement) {
      new Splide(sliderElement, {
        type: "slide",
        perPage: 5,
        perMove: 1,
        gap: "16px",
        pagination: false,
        arrows: true,
        breakpoints: {
          [Number.parseInt(breakpoint.md) - 1]: {
            perPage: 2,
            gap: "8px",
            padding: { left: "16px", right: "16px" }, // padding修正
            fixedWidth: "40%",
          },
        },
      }).mount();
    }
  } catch {
    // ネットワークエラー等の場合はセクションを非表示
    section.style.display = "none";
  }
}

// ======================================
// 商品詳細ページ：閲覧履歴スライダー
// ======================================

/**
 * 商品詳細ページの閲覧履歴スライダーの初期化
 * - トップページと同じロジックだが、セレクタが異なる
 * - 商品名を表示する専用セクションを使用
 */
async function initProductHistorySlider(): Promise<void> {
  const section = document.querySelector<HTMLElement>(
    "[data-product-history-section]",
  );
  if (!section) return;

  const list = section.querySelector<HTMLElement>(
    "[data-product-history-list]",
  );
  if (!list) return;

  // 現在の商品詳細ページの場合、そのハンドルを除外
  const currentProductHandle = (window as unknown as Record<string, unknown>)
    .__PRODUCT_HANDLE__ as string | undefined;

  // 閲覧履歴のハンドルリストを取得（現在の商品を除外）
  const handles = getViewedProductHandles(currentProductHandle);

  // 履歴がない場合はセクションを非表示
  if (handles.length === 0) {
    section.style.display = "none";
    return;
  }

  try {
    // 各商品ページから Section Rendering API で商品カード HTML を取得（並列処理）
    // 商品詳細ページ用は商品名を含む専用セクションを使用
    const fetchPromises = handles.map(async (handle: string) => {
      try {
        const response = await fetch(
          `/products/${handle}?section_id=product-history-card`,
        );
        if (!response.ok) {
          // 404 等（削除済み商品）はスキップ
          return null;
        }
        const html = await response.text();
        // DOMParser で HTML を安全にパースし、div.splide__slide タグを抽出
        const doc = new DOMParser().parseFromString(html, "text/html");
        const slide = doc.querySelector("div.splide__slide");
        return slide ? slide.outerHTML : null;
      } catch {
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);

    // null を除去して有効な HTML のみ結合
    const validHtmls = results.filter((html) => html !== null);

    // 有効な商品がない場合はセクションを非表示
    if (validHtmls.length === 0) {
      section.style.display = "none";
      return;
    }

    // リストに HTML を挿入
    list.innerHTML = validHtmls.join("");

    // セクションを表示
    section.style.display = "";

    // Splide スライダーを初期化
    const sliderElement = section.querySelector<HTMLElement>(
      "[data-product-history-slider]",
    );
    if (sliderElement) {
      new Splide(sliderElement, {
        type: "slide",
        perPage: 5,
        perMove: 1,
        gap: "16px",
        pagination: false,
        arrows: true,
        breakpoints: {
          [Number.parseInt(breakpoint.md) - 1]: {
            perPage: 2,
            gap: "8px",
            arrows: false,
          },
        },
      }).mount();
    }
  } catch {
    // ネットワークエラー等の場合はセクションを非表示
    section.style.display = "none";
  }
}

// ======================================
// 閲覧履歴一覧ページ
// ======================================

/**
 * 閲覧履歴一覧ページの初期化
 * - ローカルストレージからハンドルリストを取得
 * - 各商品ページ経由で Section Rendering API で最新の商品データを取得
 * - グリッドに商品カードを表示
 */
async function initHistoryPage(): Promise<void> {
  const grid = document.querySelector<HTMLElement>("[data-history-grid]");
  const empty = document.querySelector<HTMLElement>("[data-history-empty]");
  const loading = document.querySelector<HTMLElement>("[data-history-loading]");
  const paginationWrapper = document.querySelector<HTMLElement>(
    "[data-history-pagination]",
  );
  const paginationList = document.querySelector<HTMLElement>(
    "[data-pagination-list]",
  );

  if (!grid) return;

  // セクション設定から1ページあたりの表示数を取得（画面サイズに応じて切り替え）
  const getProductsPerPage = (): number => {
    const settings = (window as unknown as Record<string, unknown>)
      .__HISTORY_PAGE_SETTINGS__ as Record<string, unknown> | undefined;
    const isMobile = window.innerWidth < 750; // Shopifyのブレークポイント
    if (isMobile) {
      return (settings?.productsPerPageMobile as number) || 12;
    }
    return (settings?.productsPerPageDesktop as number) || 24;
  };

  const productsPerPage = getProductsPerPage();
  let currentPage = 1;
  let allProducts: string[] = []; // 全商品のHTML

  // 閲覧履歴のハンドルリストを取得
  const handles = getViewedProductHandles(undefined);

  // 履歴がない場合
  if (handles.length === 0) {
    if (loading) loading.style.display = "none";
    if (empty) empty.style.display = "";
    return;
  }

  try {
    // 各商品ページから Section Rendering API で商品カード HTML を取得（並列処理）
    const fetchPromises = handles.map(async (handle: string) => {
      try {
        const response = await fetch(
          `/products/${handle}?section_id=history-product-card-simple`,
        );
        if (!response.ok) {
          return null;
        }
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const div = doc.querySelector("div.c-product-card-simple");
        return div ? div.outerHTML : null;
      } catch {
        return null;
      }
    });

    const results = await Promise.all(fetchPromises);
    allProducts = results.filter((html) => html !== null) as string[];

    // ローディング非表示
    if (loading) loading.style.display = "none";

    // 有効な商品がない場合
    if (allProducts.length === 0) {
      if (empty) empty.style.display = "";
      return;
    }

    const totalPages = Math.ceil(allProducts.length / productsPerPage);

    // ページネーションHTML生成関数
    const renderPagination = (page: number) => {
      if (!paginationList || totalPages <= 1) return;

      let html = "";

      // 前へボタン
      if (page > 1) {
        html += `<li><a href="#" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="前のページ" data-page="${page - 1}"><span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg></span></a></li>`;
      }

      // ページ番号
      const startPage = Math.max(1, page - 2);
      const endPage = Math.min(totalPages, page + 2);

      for (let i = startPage; i <= endPage; i++) {
        if (i === page) {
          html += `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="${i}ページ">${i}</a></li>`;
        } else {
          html += `<li><a href="#" class="pagination__item link" aria-label="${i}ページ" data-page="${i}">${i}</a></li>`;
        }
      }

      // 次へボタン
      if (page < totalPages) {
        html += `<li><a href="#" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="次のページ" data-page="${page + 1}"><span class="svg-wrapper"><svg class="icon icon-caret" viewBox="0 0 10 6"><path d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg></span></a></li>`;
      }

      paginationList.innerHTML = html;

      // ページネーションのクリックイベント
      const linkElements = Array.from(
        paginationList.querySelectorAll<HTMLAnchorElement>("a[data-page]"),
      );
      for (const link of linkElements) {
        link.addEventListener("click", (e: Event) => {
          e.preventDefault();
          const pageNum = Number.parseInt(
            (link as HTMLElement).dataset.page || "1",
          );
          currentPage = pageNum;
          renderPage(currentPage);
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      }
    };

    // ページ表示関数
    const renderPage = (page: number) => {
      const startIndex = (page - 1) * productsPerPage;
      const endIndex = startIndex + productsPerPage;
      const pageProducts = allProducts.slice(startIndex, endIndex);

      // グリッドに HTML を挿入
      grid.innerHTML = pageProducts.join("");

      // ページネーション更新
      renderPagination(page);

      // ページネーション表示（2ページ以上ある場合）
      if (paginationWrapper && totalPages > 1) {
        paginationWrapper.style.display = "";
      }
    };

    // 初回ページ表示
    renderPage(currentPage);
  } catch {
    if (loading) loading.style.display = "none";
    if (empty) empty.style.display = "";
  }
}

// ======================================
// 初期化（エクスポート）
// ======================================

/**
 * 閲覧履歴機能の初期化
 * - 旧ストレージ削除（初回のみ）
 * - 各ページに応じて initProductHistory / initHistorySection / initHistoryPage を実行
 */
export function initViewedHistory(): void {
  // 旧ストレージを削除（初回実行時のみ。以降は無害）
  removeLegacyViewedProductsStorage();

  // 商品詳細ページで保存
  initProductHistory();

  // トップページで閲覧履歴セクションを表示
  initHistorySection();

  // 商品詳細ページで閲覧履歴スライダーを表示
  initProductHistorySlider();

  // 閲覧履歴一覧ページで表示
  initHistoryPage();
}
